import { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Link, useLocation } from 'react-router-dom';
import { fetchData, joinDataArrays } from '../utils/functions';
import BrowsePage from '../BrowsePage';
import TagList from '../utils/item-search/TagList';

export default function SearchResults() {
  const tagTagTypeQuery = useQuery('tagTagTypeData', () => fetchData('tag-tag-type'));
  const tagTypeQuery = useQuery('tagTypeData', () => fetchData('tag-type'));
  const tagQuery = useQuery('tagData', () => fetchData('tag'));
  const itemQuery = useQuery('itemData', () => fetchData('item'));
  const itemTagQuery = useQuery('itemTagData', () => fetchData('item-tag'));
  const featuredImageQuery = useQuery('featuredImageData', () => fetchData('featured-image'));
  const location = useLocation();
  const [filteredTagData, setFilteredTagData] = useState(null);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(999);
  const [includeShippingFilter, setIncludeShippingFilter] = useState(true);
  const [sortByListingDate, setSortByListingDate] = useState(true);
  const [sortByAlphabet, setSortByAlphabet] = useState(false);
  const [sortByPrice, setSortByPrice] = useState(false);
  const [isAscending, setIsAscending] = useState(true);
  const [includeShippingSorter, setIncludeShippingSorter] = useState(false);
  const [sortOption, setSortOption] = useState('entryDate');
  const [selectedTags, setSelectedTags] = useState({ tags: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [showSortingOptions, setShowSortingOptions] = useState(false);
  const [searchType, setSearchType] = useState(null);
  const [searchValue, setSearchValue] = useState(null);
  const [treasure, setTreasure] = useState(null);
  const [filteredTreasure, setFilteredTreasure] = useState(null);
  const [results, setResults] = useState([]);

  const sortItems = (items) => {
    const sortedItems = [...items];
  
    return sortedItems.sort((a, b) => {
      if (sortByListingDate) {
        const dateA = new Date(a.entry_date);
        const dateB = new Date(b.entry_date);
        return isAscending ? dateA - dateB : dateB - dateA;
      } else if (sortByPrice) {
        const priceA = includeShippingSorter
          ? parseFloat(a.minimum_return) + parseFloat(a.estimated_shipping)
          : parseFloat(a.minimum_return);
        const priceB = includeShippingSorter
          ? parseFloat(b.minimum_return) + parseFloat(b.estimated_shipping)
          : parseFloat(b.minimum_return);
        return isAscending ? priceA - priceB : priceB - priceA;
      } else if (sortByAlphabet) {
        const nameA = a.description.toLowerCase();
        const nameB = b.description.toLowerCase();
        
        return isAscending ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
      }
    });
  };

  useEffect(() => {
    if (featuredImageQuery.data && itemTagQuery.data && itemQuery.data && tagQuery.data) {
      const itemTagsWithTagNames = joinDataArrays(
        itemTagQuery.data,
        'tag_id',
        tagQuery.data,
        'id',
        { name : 'name' }
      );
      const itemsWithTags = joinDataArrays(
        itemQuery.data,
        'id',
        itemTagsWithTagNames,
        'item_id',
        { id: 'tag_id', name: 'name' },
        'tags'
      );
      const itemsWithFeaturedImages = joinDataArrays(
        itemsWithTags,
        'id',
        featuredImageQuery.data,
        'item_id',
        { featured_image: 'image' }
      );
      setTreasure(itemsWithFeaturedImages);
    }
  }, [featuredImageQuery.data, itemTagQuery.data, itemQuery.data, tagQuery.data]);

  useEffect(() => {
    if (tagTagTypeQuery.data && tagTypeQuery.data && tagQuery.data && filteredTreasure && selectedTags) {
      const tagTagTypesWithTagTypeNames = joinDataArrays(tagTagTypeQuery.data, 'tag_type_id', tagTypeQuery.data, 'id', { type: 'type' });
      const tagsWithTagTypes = joinDataArrays(tagQuery.data, 'id', tagTagTypesWithTagTypeNames, 'tag_id', { id: 'tag_type_id', type: 'type' }, 'tagTypes');

      const tagIdsInTreasures = filteredTreasure.reduce((acc, item) => {
        item.tags.forEach(tag => {
          if (!acc.includes(tag.id)) {
            acc.push(tag.id);
          }
        });
        return acc;
      }, []);

      const filteredTags = tagsWithTagTypes.filter(tag =>
        tagIdsInTreasures.includes(tag.id)
      );

      setFilteredTagData(filteredTags);
    }
  }, [tagTagTypeQuery.data, tagTypeQuery.data, tagQuery.data, filteredTreasure, selectedTags]);

  useEffect(() => {
    if (filteredTreasure && selectedTags) {
      if (selectedTags.tags.length > 0) {
        const filteredResults = filteredTreasure.filter(treasure => {
          return treasure.tags.some(treasureTag =>
            selectedTags.tags.some(selectedTag => selectedTag.id === treasureTag.id)
          );
        });
    
        setResults(filteredResults);
      } else {
        setResults(filteredTreasure)
      }
    }
  }, [filteredTreasure, selectedTags]);

  useEffect(() => {
    if (treasure) {
      const searchCriteria = decodeURIComponent(location.pathname.split(new RegExp(`\\${'='}`))[1]);
      let result;

      if (location.pathname.startsWith('/cats/search/des=')) {
        setSearchType('Key Word');
        setSearchValue(searchCriteria);
        result = treasure.filter(item =>
          item.description.toLowerCase().includes(searchCriteria.toLowerCase())
        );
      } else if (location.pathname.startsWith('/cats/search/tag=')) {
        setSearchType('Tag');
        setSearchValue(tagQuery.data.find(tag => tag.id === Number(searchCriteria)).name);
        result = treasure.filter(item =>
          item.tags.some(tag => tag.id === Number(searchCriteria))
        );
      } else if (location.pathname.startsWith('/cats/search/inclusive-tags=')) {
        const tagNames = tagQuery.data
          .filter(tag => searchCriteria.split(',').map(Number).includes(tag.id))
          .map(tag => tag.name);
      
        const formattedTags = tagNames.reduce((acc, tag, index) => {
          if (index === 0) return tag;
          if (index === tagNames.length - 1) return `${acc} and ${tag}`;
          return `${acc}, ${tag}`;
        }, '');
      
        setSearchType('Inclusive Tag');
        setSearchValue(formattedTags);
      
        // Inclusive search (one or more tags)
        result = treasure.filter(item => 
          item.tags.some(tag => searchCriteria.split(',').map(Number).includes(tag.id))
        );
      } else if (location.pathname.startsWith('/cats/search/exclusive-tags=')) {
        const searchTagIds = searchCriteria.split(',').map(Number);
      
        const tagNames = tagQuery.data
          .filter(tag => searchTagIds.includes(tag.id))
          .map(tag => tag.name);
      
        const formattedTags = tagNames.reduce((acc, tag, index) => {
          if (index === 0) return tag;
          if (index === tagNames.length - 1) return `${acc} and ${tag}`;
          return `${acc}, ${tag}`;
        }, '');
      
        setSearchType('Exclusive Tag');
        setSearchValue(formattedTags);
      
        // Exclusive search (must have all tags)
        result = treasure.filter(item =>
          searchTagIds.every(tagId => 
            item.tags.some(tag => tag.id === tagId)
          )
        );
      }

      // Apply price and shipping filters
      result = result.filter(item => {
        const totalPrice = parseFloat(item.minimum_return) + (includeShippingFilter ? parseFloat(item.estimated_shipping) : 0);
        return totalPrice >= minPrice && totalPrice <= maxPrice;
      });

      setFilteredTreasure(result);
    }
  }, [location.pathname, tagQuery.data, treasure, minPrice, maxPrice, includeShippingFilter]);

  const handleTagClick = (clickedTag) => {
    const isTagSelected = selectedTags.tags.some(tag => tag.id === clickedTag.id);
    const updatedTags = isTagSelected
      ? selectedTags.tags.filter(tag => tag.name !== clickedTag.name)
      : [...selectedTags.tags, clickedTag];
    setSelectedTags((prevData) => ({
      ...prevData,
      tags: updatedTags,
    }));
  };

  const handleClearResultFilters = () => {
    setMinPrice(0)
    setMaxPrice(999)
    setIncludeShippingFilter(true)
    setSelectedTags({ tags: [] })
  }

  const handleSortChange = (e) => {
    const selectedOption = e.target.value;
    setSortOption(selectedOption);

    if (selectedOption === 'entryDate') {
      handleToggleSortByEntryDate();
    } else if (selectedOption === 'alphabet') {
      handleToggleSortByAlphabet();
    } else if (selectedOption === 'price') {
      handleToggleSortByPrice();
    }
  };

  const handleToggleSortByEntryDate = () => {
    setSortByListingDate(!sortByListingDate);
    setSortByAlphabet(false);
    setSortByPrice(false);
  };

  const handleToggleSortByAlphabet  = () => {
    setSortByAlphabet (!sortByAlphabet );
    setSortByListingDate(false);
    setSortByPrice(false);
  };

  const handleToggleSortByPrice = () => {
    setSortByPrice(!sortByPrice);
    setSortByAlphabet (false);
    setSortByListingDate (false);
  };

  const handleToggleSortDirection = () => {
    setIsAscending(!isAscending);
  };

  const handleToggleIncludeShippingSorter = () => {
    setIncludeShippingSorter(!includeShippingSorter);
  };

  return (
    <div>
      <div>
        <BrowsePage />
        <br/>
      </div>

      <div>
        {searchType && searchValue && (`${searchType} Search Results for "${searchValue}"`)}
      </div>

      <button onClick={() => setShowFilters(!showFilters)}>{!showFilters ? 'Show Result Filters' : 'Hide Result Filters'}</button>
      {showFilters && (
        <div>
          <button
            onClick={() => handleClearResultFilters()}
            disabled={minPrice === 0 && maxPrice === 999 && includeShippingFilter === true && selectedTags.tags.length === 0}
          >
            Reset Result Filters
          </button>
          <div className="filters">
            <label>Min Price: </label>
            <input 
              type="number" 
              value={minPrice} 
              onChange={(e) => setMinPrice(Number(e.target.value))} 
              min={0}
              max={998} 
            />
            <label>Max Price: </label>
            <input 
              type="number" 
              value={maxPrice} 
              onChange={(e) => setMaxPrice(Number(e.target.value))} 
              min={1} 
              max={999} 
            />
            
            <label>
              Include Shipping in Price Filter:
              <input 
                type="checkbox" 
                checked={includeShippingFilter} 
                onChange={() => setIncludeShippingFilter(!includeShippingFilter)} 
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    setIncludeShippingFilter(!includeShippingFilter);
                  }
                }}
              />
            </label>
          </div>
          
          <TagList
            itemFormData={selectedTags}
            completeTagData={filteredTagData}
            tagTypeData={tagTypeQuery.data}
            onTagClick={handleTagClick}
          />
          {selectedTags.tags.length > 0 && (
            <button onClick={() => setSelectedTags({ tags: [] })}>Clear Tags</button>
          )}
        </div>
      )}

      <div>
        <button onClick={() => setShowSortingOptions(!showSortingOptions)}>{!showSortingOptions ? 'Show Sorting Options' : 'Hide Sorting Options'}</button>
      </div>
      {showSortingOptions && (
        <div>
          <div>
            <select
              id="sortOptions"
              value={sortOption}
              onChange={handleSortChange}
            >
              <option value="entryDate">Listing Date</option>
              <option value="alphabet">Alphabetical</option>
              <option value="price">Prices</option>
            </select>
          </div>
            {sortByListingDate && (
              <div>
                <button
                  onClick={handleToggleSortDirection}
                  style={{ backgroundColor: isAscending ? '#4ca7af' : '#f0f0f0', color: isAscending ? 'white' : 'black' }}
                >
                  Newest-Oldest
                </button>
                <button
                  onClick={handleToggleSortDirection}
                  style={{ backgroundColor: !isAscending ? '#4ca7af' : '#f0f0f0', color: !isAscending ? 'white' : 'black' }}
                >
                  Oldest-Newest
                </button>
              </div>
            )}
            {sortByAlphabet && (
              <div>
                <button
                  onClick={handleToggleSortDirection}
                  style={{ backgroundColor: isAscending ? '#4ca7af' : '#f0f0f0', color: isAscending ? 'white' : 'black' }}
                >
                  A-Z
                </button>
                <button
                  onClick={handleToggleSortDirection}
                  style={{ backgroundColor: !isAscending ? '#4ca7af' : '#f0f0f0', color: !isAscending ? 'white' : 'black' }}
                >
                  Z-A
                </button>
              </div>
            )}
            {sortByPrice && (
              <div>
                <div>
                  <button
                    onClick={handleToggleSortDirection}
                    style={{ backgroundColor: isAscending ? '#4ca7af' : '#f0f0f0', color: isAscending ? 'white' : 'black' }}
                  >
                    Ascending
                  </button>
                  <button
                    onClick={handleToggleSortDirection}
                    style={{ backgroundColor: !isAscending ? '#4ca7af' : '#f0f0f0', color: !isAscending ? 'white' : 'black' }}
                  >
                    Descending
                  </button>
                </div>
                <div>
                  <button
                    onClick={handleToggleIncludeShippingSorter}
                    style={{ backgroundColor: includeShippingSorter ? '#4ca7af' : '#f0f0f0', color: includeShippingSorter ? 'white' : 'black' }}
                  >
                    With Shipping
                  </button>
                  <button
                    onClick={handleToggleIncludeShippingSorter}
                    style={{ backgroundColor: !includeShippingSorter ? '#4ca7af' : '#f0f0f0', color: !includeShippingSorter ? 'white' : 'black' }}
                  >
                    Base Price Only
                  </button>
                </div>
              </div>
            )}
        </div>
      )}

      <div className="items">
        {results.length > 0 ? (
          (sortByPrice || sortByAlphabet || sortByListingDate ? sortItems(results) : results).map((treasure) => (
            <div key={treasure.id} style={{ border: '1px solid #ccc', margin: '1%' }}>
              <h3>{treasure.description}</h3>
              <div>Estimated Total: {`$${(parseFloat(treasure.minimum_return) + parseFloat(treasure.estimated_shipping)).toFixed(2)}`}</div>
              <div>Estimated Price: {`$${treasure.minimum_return}`}</div>
              <div>Estimated Shipping: {`$${treasure.estimated_shipping}`}</div>
              <Link to={`/cats/item/${treasure.id}`}>
                <img
                  src={`${import.meta.env.VITE_API_URL}image/${treasure.featured_image}`}
                  alt={treasure.featured_image}
                  className="img"
                />
              </Link>
            </div>
          ))
        ) : (
          <div style={{color: 'yellow'}}>No Matches Found</div>
        )}
      </div>
    </div>
  );
}
